import { Clock, Eye, Film, Layers } from 'lucide-react';
import { formatDuration } from './videoEmbed';

interface RecordedClassesStatsProps {
  summary?: {
    lessons: number;
    topics: number;
    totalMinutes: number;
    totalViews: number;
  };
  /**
   * A date or topic filter is on. The server works the summary out from the
   * filtered list, so the tiles must not read as the whole channel.
   */
  isFiltered?: boolean;
  /**
   * Lessons posted with no running time. They add nothing to the total, so the
   * tile says how many rather than passing off a part as the whole.
   */
  lessonsWithoutLength?: number;
}

/**
 * Mirrors `MAX_RECORDINGS_RETURNED` on the server: the list, and so the summary,
 * stops at this many lessons, and a count at the cap is "at least", not "all".
 */
const RECORDINGS_RETURNED_CAP = 200;

function formatStatCount(value: number | string): string | number {
  if (typeof value !== 'number') return value;
  if (value === 0) return '0';
  if (value > 0 && value < 10) return `0${value}`;
  return value;
}

export function RecordedClassesStats({
  summary,
  isFiltered = false,
  lessonsWithoutLength = 0,
}: RecordedClassesStatsProps) {
  const lessons = summary?.lessons ?? 0;
  const lessonsSubtext = isFiltered
    ? 'Matching your filters'
    : lessons >= RECORDINGS_RETURNED_CAP
      ? `Latest ${RECORDINGS_RETURNED_CAP} shown`
      : 'All recordings';

  const cards = [
    {
      label: isFiltered ? 'Lessons shown' : 'Lessons posted',
      value: lessons,
      subtext: lessonsSubtext,
      icon: Film,
      containerClass:
        'card bg-blue-50/70 dark:bg-blue-950/20 border-blue-200/80 dark:border-blue-900/40',
      iconContainerClass:
        'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/25',
      titleClass: 'text-blue-800 dark:text-blue-300',
      valueClass: 'text-blue-600 dark:text-blue-400',
      subtextClass: 'text-blue-600/80 dark:text-blue-400/80',
    },
    {
      // Distinct topic names typed on the lessons — not chapters of a syllabus.
      label: 'Topics',
      value: summary?.topics ?? 0,
      subtext: 'Named on these lessons',
      icon: Layers,
      containerClass:
        'card bg-emerald-50/70 dark:bg-emerald-950/20 border-emerald-200/80 dark:border-emerald-900/40',
      iconContainerClass:
        'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25',
      titleClass: 'text-emerald-800 dark:text-emerald-300',
      valueClass: 'text-emerald-600 dark:text-emerald-400',
      subtextClass: 'text-emerald-600 dark:text-emerald-400',
    },
    {
      label: 'Teaching posted',
      value: summary?.totalMinutes ? formatDuration(summary.totalMinutes) ?? '—' : '—',
      // The lessons' own running time added up, not how long anybody watched.
      subtext:
        lessonsWithoutLength > 0
          ? `Total length (${lessonsWithoutLength} without a length)`
          : 'Total length',
      icon: Clock,
      containerClass:
        'card bg-indigo-50/70 dark:bg-indigo-950/20 border-indigo-200/80 dark:border-indigo-900/40',
      iconContainerClass:
        'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-500/25',
      titleClass: 'text-indigo-800 dark:text-indigo-300',
      valueClass: 'text-indigo-600 dark:text-indigo-400',
      subtextClass: 'text-indigo-600 dark:text-indigo-400',
    },
    {
      // Each lesson counts a student once however often they replay it, so this
      // is distinct watchers per lesson, added up — not plays.
      label: 'Student views',
      value: summary?.totalViews ?? 0,
      subtext: 'One per student per lesson',
      icon: Eye,
      containerClass:
        'card bg-amber-50/70 dark:bg-amber-950/20 border-amber-200/80 dark:border-amber-900/40',
      iconContainerClass:
        'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/25',
      titleClass: 'text-amber-800 dark:text-amber-300',
      valueClass: 'text-amber-600 dark:text-amber-400',
      subtextClass: 'text-amber-600 dark:text-amber-400',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3.5">
      {cards.map((card) => {
        const IconComponent = card.icon;
        return (
          <div
            key={card.label}
            className={`${card.containerClass} rounded-[14px] sm:rounded-[16px] p-2.5 sm:px-4 sm:py-2.5 flex items-center gap-2.5 sm:gap-3.5 shadow-2xs`}
          >
            <span
              className={`grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-[10px] sm:rounded-[12px] ${card.iconContainerClass}`}
            >
              <IconComponent className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
            </span>
            <div className="min-w-0 flex flex-col justify-center">
              <span
                className={`text-[12px] sm:text-[14px] font-bold ${card.titleClass} tracking-wide block truncate`}
              >
                {card.label}
              </span>
              <p className={`text-2xl font-black ${card.valueClass} mt-0.5 leading-tight`}>
                {formatStatCount(card.value)}
              </p>
              <span
                className={`text-2xs sm:text-xs ${card.subtextClass} font-semibold mt-0.5 block truncate`}
              >
                {card.subtext}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
